import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { format, addDays, subDays } from "date-fns";
import { getUTCRangeForLocalDate } from "@/lib/timezoneUtils";

interface GameCount {
  date: string;
  count: number;
  hasLive: boolean;
}

interface UseGameCountsOptions {
  startDate?: Date;
  days?: number;
  leagueId?: number;
}

export function useGameCounts(options: UseGameCountsOptions = {}) {
  const [gameCounts, setGameCounts] = useState<Record<string, number>>({});
  const [liveDays, setLiveDays] = useState<Record<string, boolean>>({});
  const [loading, setLoading] = useState(true);

  const {
    startDate = subDays(new Date(), 6), // 6 dias antes de hoje
    days = 14, // 6 antes + hoje + 7 depois = 14 dias total
    leagueId
  } = options;

  useEffect(() => {
    fetchGameCounts();
  }, [startDate, days, leagueId]);

  const fetchWithRetry = async (
    queryFn: () => Promise<any>,
    maxRetries = 3,
    initialDelay = 1000
  ) => {
    let lastError;
    
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        return await queryFn();
      } catch (error) {
        lastError = error;
        
        if (attempt < maxRetries - 1) {
          const delay = initialDelay * Math.pow(2, attempt);
          console.log(`Retry ${attempt + 1}/${maxRetries} after ${delay}ms...`);
          await new Promise(resolve => setTimeout(resolve, delay));
        }
      }
    }
    
    throw lastError;
  };

  const fetchGameCounts = async () => {
    try {
      setLoading(true);
      
      // Generate date range
      const dateFrom = format(startDate, 'yyyy-MM-dd');
      const dateTo = format(addDays(startDate, days), 'yyyy-MM-dd');

      // Expand UTC range to capture all games that could appear in local timezone
      const { utcFrom, utcTo } = getUTCRangeForLocalDate(dateFrom, dateTo);

      // Use retry logic for the query
      const { data, error } = await fetchWithRetry(async () => {
        let query = supabase
          .from('games')
          .select('match_date, status')
          .gte('match_date', utcFrom)
          .lt('match_date', utcTo);

        // Apply league filter if provided
        if (leagueId) {
          const { data: leagueData } = await supabase
            .from('leagues')
            .select('id')
            .eq('api_football_id', leagueId)
            .single();
          
          if (leagueData) {
            query = query.eq('league_id', leagueData.id);
          }
        }

        return await query;
      });

      if (error) {
        console.error('Error fetching game counts:', error);
        // Don't crash preview, just keep previous state if available
        if (Object.keys(gameCounts).length === 0) {
          setGameCounts({});
          setLiveDays({});
        }
        return;
      }

      // Count games per date and track live games
      const counts: Record<string, number> = {};
      const liveGameDays: Record<string, boolean> = {};
      
      // Initialize all dates with 0
      for (let i = 0; i < days; i++) {
        const date = format(addDays(startDate, i), 'yyyy-MM-dd');
        counts[date] = 0;
        liveGameDays[date] = false;
      }

      // Count actual games and detect live games
      data?.forEach((game: any) => {
        const gameDate = format(new Date(game.match_date), 'yyyy-MM-dd');
        if (counts.hasOwnProperty(gameDate)) {
          counts[gameDate]++;
          if (game.status === 'live') {
            liveGameDays[gameDate] = true;
          }
        }
      });

      setGameCounts(counts);
      setLiveDays(liveGameDays);
    } catch (error) {
      console.error('Error in fetchGameCounts (after retries):', error);
      // Just log, don't crash preview - keep previous state
    } finally {
      setLoading(false);
    }
  };

  const getCountForDate = (date: Date): number => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return gameCounts[dateKey] || 0;
  };

  const hasLiveGamesForDate = (date: Date): boolean => {
    const dateKey = format(date, 'yyyy-MM-dd');
    return liveDays[dateKey] || false;
  };

  return {
    gameCounts,
    liveDays,
    loading,
    getCountForDate,
    hasLiveGamesForDate,
    refresh: fetchGameCounts
  };
}